import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, message, Modal, Spin, Switch, Tabs, Typography } from 'antd';
import { HolderOutlined, TeamOutlined } from '@ant-design/icons';
import AppCard from '../../../components/cards/AppCard';
import ParamListView from '../../../components/ParamListView';
import {
  getAllTeams,
  getTeamModuleFields,
  updateTeam,
  updateTeamModuleFieldConfig,
} from '../../../services/adminApi';
import '../../../styles/UserSettingsSection.css';

const { Text } = Typography;

const MODULE_TABS = [
  { key: 'job',         label: 'Jobs'        },
  { key: 'candidate',   label: 'Candidates'  },
  { key: 'submissions', label: 'Submissions' },
];

const CONFIG_TYPES = [
  { key: 'listView', label: 'List View' },
  { key: 'filter',   label: 'Filter'    },
  { key: 'form',     label: 'Form'      },
];

function FieldRow({ field, index, onToggle, onReorder }) {
  const dragIdx = useRef(null);
  const [over, setOver]         = useState(false);
  const [dragging, setDragging] = useState(false);

  function handleDragStart(e) { dragIdx.current = index; e.dataTransfer.effectAllowed = 'move'; setDragging(true); }
  function handleDragEnd()    { setDragging(false); setOver(false); }
  function handleDragOver(e)  { e.preventDefault(); setOver(true); }
  function handleDragLeave()  { setOver(false); }
  function handleDrop(e) {
    e.preventDefault(); setOver(false);
    if (dragIdx.current !== null && dragIdx.current !== index) onReorder(dragIdx.current, index);
    dragIdx.current = null;
  }

  return (
    <div
      draggable
      onDragStart={handleDragStart} onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}   onDragLeave={handleDragLeave} onDrop={handleDrop}
      className={`uss-field-row with-edit${over ? ' drag-over' : ''}${dragging ? ' dragging' : ''}`}
    >
      <span className="uss-drag-handle"><HolderOutlined /></span>
      <span className="uss-field-name">
        {field.fieldName}
        <span className="uss-field-key">{field.fieldKey}</span>
      </span>
      <span className="uss-toggle-cell">
        <Switch size="small" checked={field.isVisible}            onChange={(v) => onToggle(index, 'isVisible', v)} />
      </span>
      <span className="uss-toggle-cell">
        <Switch size="small" checked={Boolean(field.isEditable)}  onChange={(v) => onToggle(index, 'isEditable', v)} />
      </span>
      <span className="uss-toggle-cell">
        <Switch size="small" checked={Boolean(field.ismandatory)} onChange={(v) => onToggle(index, 'ismandatory', v)} />
      </span>
    </div>
  );
}

function ModuleFieldConfig({ team, configType }) {
  const [moduleFields, setModuleFields] = useState({});
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState({});
  const [activeModule, setActiveModule] = useState('job');

  useEffect(() => {
    if (!team) return;
    setLoading(true);
    getTeamModuleFields(team, configType)
      .then(setModuleFields)
      .catch(() => message.error('Failed to load field config'))
      .finally(() => setLoading(false));
  }, [team, configType]);

  const fields = moduleFields[activeModule] ?? [];

  function setFields(module, updater) {
    setModuleFields((prev) => ({
      ...prev,
      [module]: typeof updater === 'function' ? updater(prev[module] ?? []) : updater,
    }));
  }

  function handleToggle(idx, key, val) {
    setFields(activeModule, (prev) => prev.map((f, i) => i === idx ? { ...f, [key]: val } : f));
  }

  function handleReorder(from, to) {
    setFields(activeModule, (prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return arr.map((f, i) => ({ ...f, order: i }));
    });
  }

  async function handleSave() {
    const mod = activeModule;
    setSaving((p) => ({ ...p, [mod]: true }));
    try {
      await updateTeamModuleFieldConfig(team, mod, fields, configType);
      message.success('Saved');
    } catch {
      message.error('Save failed');
    } finally {
      setSaving((p) => ({ ...p, [mod]: false }));
    }
  }

  return (
    <Spin spinning={loading}>
      <Tabs size="small" activeKey={activeModule} onChange={setActiveModule}
        items={MODULE_TABS.map((t) => ({ key: t.key, label: t.label }))} />
      <div className="uss-field-header with-edit">
        <span /><span>Field</span>
        <span style={{ textAlign: 'center' }}>Visible</span>
        <span style={{ textAlign: 'center' }}>Editable</span>
        <span style={{ textAlign: 'center' }}>Mandatory</span>
      </div>
      <div className="uss-field-list">
        {fields.length === 0 && !loading && <div className="uss-empty">No fields found.</div>}
        {fields.map((field, idx) => (
          <FieldRow key={field.fieldKey ?? idx} field={field} index={idx}
            onToggle={handleToggle} onReorder={handleReorder} />
        ))}
      </div>
      <div className="uss-modal-footer">
        <Button type="primary" size="small" loading={saving[activeModule]} onClick={handleSave}>
          Save {MODULE_TABS.find((t) => t.key === activeModule)?.label}
        </Button>
      </div>
    </Spin>
  );
}

export default function TeamsSection() {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [configOpen, setConfigOpen]     = useState(false);
  const [editOpen, setEditOpen]         = useState(false);
  const [configType, setConfigType]     = useState('listView');
  const [teams, setTeams]               = useState([]);
  const [total, setTotal]               = useState(0);
  const [loading, setLoading]           = useState(false);
  const [editName, setEditName]         = useState('');
  const [editSaving, setEditSaving]     = useState(false);

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllTeams();
      setTeams(result.teams);
      setTotal(result.total);
    } catch {
      message.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTeams(); }, [fetchTeams]);

  function openConfig(team) {
    setSelectedTeam(team);
    setConfigType('listView');
    setConfigOpen(true);
  }

  function openEdit(team) {
    setSelectedTeam(team);
    setEditName(team.teamName ?? '');
    setEditOpen(true);
  }

  async function handleSaveTeamName() {
    if (!editName.trim()) { message.warning('Team name is required'); return; }
    setEditSaving(true);
    try {
      await updateTeam(selectedTeam, {
        teamName: editName.trim(),
        managerId: selectedTeam.managerId,
        memberIds: selectedTeam.memberIds,
      });
      message.success('Team updated');
      setEditOpen(false);
      fetchTeams();
    } catch {
      message.error('Failed to update team');
    } finally {
      setEditSaving(false);
    }
  }

  const fields = [
    { label: 'Team Name', value: 'teamName', width: 200 },
    { label: 'Manager',   value: 'manager',  width: 200 },
    { label: 'Members',   value: 'memberCount', width: 90, sorter: false },
    {
      label: 'Actions', value: '_actions', width: 160, sorter: false,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 4 }}>
          <Button size="small" type="link" onClick={() => openEdit(record)}>Edit</Button>
          <Button size="small" type="link" onClick={() => openConfig(record)}>Configure</Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <AppCard icon={<TeamOutlined />} title="Teams"
        style={{ marginBottom: 0 }}
        bodyStyle={{ padding: 0 }}>
        <ParamListView
          listName="Teams"
          dataSource={teams}
          fields={fields}
          loading={loading}
          total={total}
        />
      </AppCard>

      {/* Edit team name */}
      <Modal open={editOpen} onCancel={() => setEditOpen(false)}
        title={<><TeamOutlined style={{ color: 'var(--color-brand)', marginRight: 6 }} />Edit Team</>}
        width={420} footer={null} destroyOnClose>
        <div className="uss-edit-team-body" style={{ paddingTop: 12 }}>
          <div>
            <div className="uss-edit-team-label">Team Name</div>
            <Input value={editName} onChange={(e) => setEditName(e.target.value)}
              onPressEnter={handleSaveTeamName} placeholder="Enter team name" />
          </div>
          <div className="uss-modal-footer">
            <Button onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button type="primary" loading={editSaving} onClick={handleSaveTeamName}>Save</Button>
          </div>
        </div>
      </Modal>

      {/* Field config */}
      <Modal
        className="uss-modal"
        open={configOpen}
        onCancel={() => setConfigOpen(false)}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TeamOutlined style={{ color: 'var(--color-brand)' }} />
            <span>Configure: {selectedTeam?.teamName}</span>
          </div>
        }
        width={720} footer={null} destroyOnClose
      >
        <div style={{ marginBottom: 12 }}>
          <div className="uss-config-toggle">
            {CONFIG_TYPES.map((ct) => (
              <button key={ct.key}
                className={`uss-config-btn${configType === ct.key ? ' active' : ''}`}
                onClick={() => setConfigType(ct.key)}>{ct.label}</button>
            ))}
          </div>
        </div>
        {selectedTeam && (
          <ModuleFieldConfig
            key={`${selectedTeam?.teamId}-${configType}`}
            team={selectedTeam}
            configType={configType}
          />
        )}
      </Modal>
    </>
  );
}